/**
 * The words Leadership opens each view with, and the earlier figure under each headline measure (5C, DEC-37, DEC-38),
 * the same on the page and in the one-page PDF. Each opening is two or three short lines (DEC-37): where things stand
 * with the change since the earlier figure, then the goal, then the one thing needing attention. Plain words for
 * partners and the Board: CDW, the BTC Baseline and BTI are said in plain words the first time a view names them; no
 * verdicts; every figure says what it counts.
 */
import { BTI_WAVE1_EVALUATION, BTI_WAVE2_DUE, BTI_WAVE2_ITEMS, btiPositionCounts, btiVerdictCounts } from './bti-evaluation.js';
import { BASELINES } from './plain-language.js';
import { findingCounts, GOALS, isApartDot, longDate, MARCH_2025_NETWORK_LABEL, PROGRAM_NAME, rankedDots, } from './leadership.js';
const pct = (v) => `${Math.round(v)}%`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** How many requirements each goal covers: what a firm must have rated before its share can say "all" (SFH-01). */
export const GOAL_SIZE = { btc: BASELINES.btc.size, wave1: BTI_WAVE1_EVALUATION.items.length, wave2: BTI_WAVE2_ITEMS.length };
/**
 * What a firm has not rated yet against a goal's size (SFH-01): "10 of 14 rated, 4 not assessed yet", or null when
 * every requirement is rated. A share of the rated ones never reads as the whole goal while some are not rated.
 */
export function unratedNote(rated, size) {
    return rated > 0 && rated < size ? `${rated} of ${size} rated, ${size - rated} not assessed yet` : null;
}
/**
 * The first mention of each name on a view, in plain words (L1). Each gloss is in brackets (NEXT PASS 6), so it reads
 * whole wherever it sits and never runs into the words after it, as "CDW, the independent assessor, the BTC Baseline"
 * did on the network tab.
 */
export const GLOSS = {
    btc: 'the BTC Baseline (the 56 core security controls)',
    cdw: 'CDW (the independent assessor)',
    bti: 'BTI (Baker Tilly International)',
};
/** Wave 2 is the one deadline the network has this year; within this many days of it, it is what needs attention (DEC-37). */
export const WAVE2_ATTENTION_DAYS = 120;
const daysBetween = (from, to) => Math.round((Date.parse(`${to.slice(0, 10)}T12:00:00Z`) - Date.parse(`${from.slice(0, 10)}T12:00:00Z`)) / 86_400_000);
/** "1st", "2nd", "3rd", "11th". */
export const ordinalOf = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
/** Names joined as "a", "a and b", "a, b and c". */
export const joinNames = (names) => (names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);
/**
 * The least protected business risk with a figure, as "client data exposure, with 33% of its protections in place";
 * every risk tied for the lowest figure is named (SFH-09).
 */
export function weakest(rows, where = '') {
    const w = weakestRisks(rows);
    if (!w)
        return null;
    const low = w.pct;
    // Every protection in place: nothing to name, and no risk is called out for being at 100%.
    if (low >= 100)
        return `nothing at present; every business risk has all its protections in place${where}`;
    const tied = w.rows.map((r) => r.label.toLowerCase());
    return tied.length === 1 ? `${tied[0]}, with ${pct(low)} of its protections in place${where}` : `${joinNames(tied)}, each with ${pct(low)} of its protections in place${where}`;
}
/**
 * What needs attention for the network (DEC-37): BTI Wave 2 while it is within 120 days of its due date and not
 * every item is in place at the typical firm, otherwise the least protected business risk.
 */
export function networkAttention(n, asOf) {
    if (networkAttentionKind(n, asOf) === 'wave2') {
        return `Wave 2, the next ${BTI_WAVE2_ITEMS.length} requirements of ${GLOSS.bti}, due ${longDate(BTI_WAVE2_DUE)}, with ${pct(n.btiWave2Median)} in place at the typical firm`;
    }
    return weakest((n.businessRisks ?? []).map((r) => ({ label: r.label, pct: r.coveragePct })), ' at the typical firm');
}
/** Days from `asOf` to BTI's Wave 2 due date (negative once it has passed): the one count the openings and cards use. */
export const daysToWave2 = (asOf) => daysBetween(asOf, BTI_WAVE2_DUE);
/** Which rule networkAttention follows (5D cards reuse it): Wave 2 within 120 days of its date, else the least protected risk. */
export function networkAttentionKind(n, asOf) {
    const toWave2 = daysToWave2(asOf);
    return toWave2 >= 0 && toWave2 <= WAVE2_ATTENTION_DAYS && n.btiWave2Median !== null && n.btiWave2Median < 100 ? 'wave2' : 'risk';
}
/** Which rule firmAttention follows (5D cards reuse it): a plan off track, no 2026 rating yet, else the least protected risk. */
export function firmAttentionKind(f) {
    if (f.headline.plans.offTrack)
        return 'off_track';
    if (f.source === null && f.march2025Baselines)
        return 'no_rating';
    return 'risk';
}
/** What needs attention for one firm (DEC-37): any action plan off track, otherwise its least protected business risk. */
export function firmAttention(f) {
    const kind = firmAttentionKind(f);
    if (kind === 'off_track')
        return `${plural(f.headline.plans.offTrack, 'action plan')} off track`;
    if (kind === 'no_rating')
        return f.inCdwProgramme === false ? `a first 2026 rating, as the firm is not in ${PROGRAM_NAME}` : 'its CDW assessment, which is not complete yet';
    return weakest(f.businessRisks.map((r) => ({ label: r.label, pct: r.coveragePct })));
}
/**
 * The least protected business risk with a figure, as data (the rule weakest says in words): every risk tied for the
 * lowest rounded figure, and that figure; null when none has a figure.
 */
export function weakestRisks(rows) {
    const rated = rows.filter((r) => r.pct !== null);
    if (!rated.length)
        return null;
    const low = Math.min(...rated.map((r) => Math.round(r.pct)));
    return { rows: rated.filter((r) => Math.round(r.pct) === low), pct: low };
}
/**
 * The one direction rule (DEC-29, like for like), for the Summary, the network and BTI tabs, the tiles and the PDF: an
 * earlier figure that counted fully in place only (the March 2025 survey's "achieved", quarters before 2026-09-26) is
 * set against today's fully figure; one that counted partly too, against today's in-place figure. Null when today's
 * comparable figure is not known, so a direction is never guessed and never "up" when like for like it is down.
 */
export function likeForLike(now, then, thenCountsPartly) {
    const v = thenCountsPartly ? now.inPlace : now.fully;
    if (v === null || v === undefined)
        return null;
    return { points: Math.round(v - then), fullyOnly: !thenCountsPartly };
}
/** "up 9 points", "down 1 point" or "level". */
export const movePhrase = (points) => (points === 0 ? 'level' : `${points > 0 ? 'up' : 'down'} ${plural(Math.abs(points), 'point')}`);
/**
 * The one wording for a change counting fully in place only (L1, NEXT PASS 6): today's fully figure, then its change,
 * "38% fully, down 10 points". The tiles, the trend cards, every opening and the PDF say it this way and no other.
 */
export const fullyChangePhrase = (fullyNow, points) => `${pct(fullyNow)} fully, ${movePhrase(points)}`;
/**
 * "38% fully, down 10 points" (an earlier figure that counted fully in place only: fullyChangePhrase), else "up 5
 * points"; null when unknown.
 */
export function direction(now, then, thenCountsPartly) {
    const c = likeForLike(now, then, thenCountsPartly);
    if (!c)
        return null;
    return c.fullyOnly ? fullyChangePhrase(now.fully, c.points) : movePhrase(c.points);
}
/** The last day of a stored quarter ("2026-Q2" is June 30, 2026), so openings say a date, never "2026 Q2". */
export const quarterEnd = (q) => {
    const [y, n] = [q.slice(0, 4), Number(q.slice(-1))];
    return `${y}-${['03-31', '06-30', '09-30', '12-31'][n - 1]}`;
};
/**
 * A stored quarter as a Board reader sees it (L2, NEXT PASS 4), the one formatter for the openings, the tiles, the
 * trend's words and the PDF: its last day, "June 30, 2026"; "short" for a chart's axis, where a full date does not
 * fit: "Jun 2026".
 */
export const quarterDate = (q, form = 'long') => {
    const d = longDate(quarterEnd(q));
    return form === 'short' ? `${d.slice(0, 3)} ${q.slice(0, 4)}` : d;
};
/**
 * A change against the March 2025 survey in both figures (DEC-29, DEC-45): the headline change (today's figure
 * counting fully or partly in place, against the survey's achieved) and the like-for-like change (fully in place
 * only), each with its own direction, even when the two point different ways. Both come from likeForLike. Null when
 * today's in-place figure is not known; fullyOnly, and fully (today's fully figure), are null when today's fully
 * figure is not known.
 */
export function surveyChange(now, then) {
    const headline = likeForLike(now, then, true);
    if (!headline)
        return null;
    const f = likeForLike(now, then, false);
    return { headline: headline.points, fullyOnly: f?.points ?? null, fully: f ? now.fully : null };
}
/**
 * The one wording for a change against the survey (DEC-45, NEXT PASS 6), for the openings, the tiles, the trend card
 * and the PDF: the headline change first, then today's fully figure with its own change (fullyChangePhrase), each
 * beside its own figure: "up 7 points since the March 2025 survey; 38% fully, down 10 points". `since` is what the
 * change is since ("the March 2025 survey", or "then" on a tile).
 */
export function surveyChangePhrase(c, since) {
    const fully = c.fullyOnly === null || c.fully === null ? '' : `; ${fullyChangePhrase(c.fully, c.fullyOnly)}`;
    return `${movePhrase(c.headline)} since ${since}${fully}`;
}
/** "the March 2025 survey": one name for it in every opening (L1, NEXT PASS 5). */
export const SURVEY = 'the March 2025 survey';
/** What the goal counts (DEC-36: in place, fully or partly), said on an opening's goal line. */
export const PARTLY_COUNTS = 'partly in place counts toward it';
/**
 * Why the two changes can point different ways, said on the goal line as what the goal counts and what the survey
 * did not. True whatever the figures are (NEXT PASS 6). Short, so the network Summary's goal line stays one line and
 * its four tiles stay on the first 1280 x 800 screen (5C L2).
 */
export const COUNTING_NOTE = `${PARTLY_COUNTS}, unlike in ${SURVEY}`;
/**
 * What follows an opening's headline figure (DEC-36, DEC-37, DEC-45): its change since the survey, then today's
 * fully figure with that figure's own change (surveyChangePhrase); or today's fully figure alone when there is nothing
 * to compare. `compared` is true when both changes are said. Empty text when neither is known.
 */
function changeBeside(now, then) {
    const c = then === null || then === undefined ? null : surveyChange(now, then);
    if (c)
        return { text: `, ${surveyChangePhrase(c, SURVEY)}`, compared: c.fullyOnly !== null };
    return { text: now.fully === null || now.fully === undefined ? '' : `, ${pct(now.fully)} fully`, compared: false };
}
/** An opening's goal line: the goal, what it counts, and what the survey counted when both changes against it are said. */
const goalLine = (goal, compared) => `Goal: ${goal}; ${compared ? COUNTING_NOTE : PARTLY_COUNTS}.`;
const NO_CHANGE = { text: '', compared: false };
/**
 * The Summary's opening lines, for the picked firm or the network (L1, DEC-37: two or three lines). Line 1: the
 * position and its change: today's figure, its change since the survey, and today's fully figure with its own change
 * (DEC-36, DEC-45). Line 2: the goal, what it counts and what the survey counted. Line 3: what needs attention.
 */
export function summaryLines(d) {
    if (d.firm) {
        const f = d.firm;
        const b = f.headline.btc;
        const s = f.source === null ? f.march2025Baselines : null;
        const fully = b.total ? ((b.inPlace - b.partly) / b.total) * 100 : null;
        const change = !s && b.value !== null ? changeBeside({ inPlace: b.value, fully }, b.march2025) : NO_CHANGE;
        const where = s
            ? s.btc.pct === null
                ? `${f.firmName} has no 2026 results yet, and its March 2025 survey has no answers for ${GLOSS.btc}.`
                : `${f.firmName} has no 2026 results yet; its March 2025 survey answers put ${pct(s.btc.pct)} of ${GLOSS.btc} in place.`
            : b.value === null
                ? `${f.firmName} has no results yet for ${GLOSS.btc}.`
                : b.total < GOAL_SIZE.btc
                    ? // A share of the rated controls only, with what is not rated yet (SFH-01).
                        `${f.firmName} has ${pct(b.value)} of the ${b.total} rated controls of ${GLOSS.btc} in place${f.source === 'self' ? ' by its own rating' : ''}${change.text}; ${GOAL_SIZE.btc - b.total} not assessed yet.`
                    : `${f.firmName} has ${pct(b.value)} of ${GLOSS.btc} in place${f.source === 'self' ? ' by its own rating' : ''}${change.text}.`;
        const att = firmAttention(f);
        return [where, goalLine(`${GOALS.btc} (network goal)`, change.compared), ...(att ? [`Needs attention: ${att}.`] : [])];
    }
    const n = d.network;
    const change = n.btcMedian === null ? NO_CHANGE : changeBeside({ inPlace: n.btcMedian, fully: n.fullyMedians?.btc }, n.march2025BtcMedian);
    const where = n.btcMedian === null
        ? `The typical firm's share of ${GLOSS.btc} is shown once five or more firms have CDW results.`
        : `The typical firm has ${pct(n.btcMedian)} of ${GLOSS.btc} in place${change.text}.`;
    const att = networkAttention(n, d.asOf);
    return [where, goalLine(GOALS.btc, change.compared), ...(att ? [`Needs attention: ${att}.`] : [])];
}
/**
 * The member firms a network figure is out of (L5): the network's member firms; firms not in the portal yet count
 * as "nothing yet" in the evidence counts, so the counts and every "of N firms" share this one denominator.
 */
export const memberFirms = (n) => n.evidence ? n.evidence.cdw + n.evidence.self + n.evidence.survey + n.evidence.none : n.firmsInNetwork;
/**
 * The member firms by what their figures rest on (L5), for "What was assessed" on the page and in the PDF: the counts
 * out of the member firms (memberFirms), each group's firms by name when the caller may see names (National and
 * Board), and how many member firms are not in the portal yet (counted under "nothing yet", never named).
 */
export function evidenceGroups(n) {
    const counts = n.evidence ?? { cdw: n.firmsWithResults, self: 0, survey: 0, none: Math.max(0, n.firmsInNetwork - n.firmsWithResults) };
    if (!n.firms?.length)
        return { counts, names: {}, notInPortal: 0 };
    const names = {};
    for (const f of [...n.firms].sort((a, b) => a.firmName.localeCompare(b.firmName)))
        (names[f.source] ??= []).push(f.firmName);
    return { counts, names, notInPortal: Math.max(0, counts.none - (names.none?.length ?? 0)) };
}
/** What each member firm's figures rest on, one wording for the page and the PDF ("What was assessed", L5). */
export const EVIDENCE_LABELS = {
    cdw: 'CDW results (independent)',
    self: 'Own rating, waiting for CDW',
    survey: 'March 2025 survey answers only',
    none: 'Nothing yet',
};
/** What the CDW assessment covers and does not yet, said beside what was assessed (page and PDF). */
export const CDW_SCOPE = {
    assesses: 'the 56 BTC Baseline controls and 7 key protections, a penetration test (a controlled attempt to break in) and a risk assessment report',
    notCovered: 'how quickly an attack would be noticed, how an attacker could move between systems, and whether backups have been restored in a test',
};
/** "BT Demo's figures rest on CDW results (independent)." for one firm's "What was assessed" (page and PDF). */
export function firmEvidenceLine(f) {
    const what = f.source === 'cdw' ? 'CDW results (independent)' : f.source === 'self' ? 'its own rating, while it waits for CDW' : f.march2025Baselines ? 'its March 2025 survey answers only' : 'nothing yet';
    return `${f.firmName}'s figures rest on ${what}.`;
}
/**
 * Findings closed and open since CDW raised its first ones, one form for a firm and the network (L1, NEXT PASS 6):
 * "since its first findings, raised on May 4, 2026, 3 have closed and 17 are open". "Since" says since when, in words:
 * the earliest date a finding on the list was raised; without dates, "in its findings list(s)".
 */
function findingsSince(list, lists) {
    const first = list.map((x) => x.raisedOn).filter((v) => Boolean(v)).sort()[0];
    const c = findingCounts(list);
    const since = first ? `since its first findings, raised on ${longDate(first)}, ` : `in its findings ${lists}, `;
    return `${since}${c.closed || 'none'} ${c.closed === 1 ? 'has' : 'have'} closed and ${c.open || 'none'} ${c.open === 1 ? 'is' : 'are'} open`;
}
/**
 * The goal for a firm with no independent report yet (L1): the assessment and its findings list. DEC-53: owners and due
 * dates stay on the firm's own pages, so the goal no longer names them.
 */
export const NO_REPORT_GOAL = 'an independent CDW assessment and its findings list';
/** What a firm waiting for CDW needs next: its assessment's start, from the tracker (cdwStart). */
function cdwNext(f) {
    const s = f.program?.cdwStart;
    if (s?.status === 'planned' && s.date)
        return `the CDW assessment, planned to start on ${longDate(s.date)}`;
    if (s?.status === 'started')
        return `the CDW risk assessment report, which comes with the findings`;
    return 'a date for the CDW assessment';
}
/** The Assessment program's opening lines, for the picked firm or the network (L1). */
export function assessmentLines(d) {
    if (d.firm) {
        const f = d.firm;
        const a = f.assessments.lastIndependent;
        if (!a) {
            const goal = `Goal: ${NO_REPORT_GOAL}.`;
            if (f.inCdwProgramme === false)
                return [`${f.firmName} is not in ${PROGRAM_NAME}, the program of ${GLOSS.cdw}, so it has no findings list yet.`, goal, `Needs attention: ${firmAttention(f) ?? `a first 2026 rating, as the firm is not in ${PROGRAM_NAME}`}.`];
            const where = f.source === 'self' ? `${f.firmName} has rated itself while it waits for ${GLOSS.cdw}.` : `${f.firmName}'s assessment by ${GLOSS.cdw} is not complete yet.`;
            return [where, goal, `Needs attention: ${cdwNext(f)}.`];
        }
        // DEC-53: the tab opens with what CDW found, then where the firm is in the program; owners and due dates stay on
        // the firm's own pages, so there is no goal or attention line about them. "Is open" only once the register has arrived (SFH-02).
        if (!f.findings.length)
            return [`${GLOSS.cdw} reported on ${f.firmName}${a.date ? ` on ${longDate(a.date)}` : ''}; its findings list has not been received yet.`];
        return [`${GLOSS.cdw} has reported on ${f.firmName}; ${findingsSince(f.findings, 'list')}.`];
    }
    const n = d.network;
    const e = n.evidence ?? { cdw: n.firmsWithResults, self: 0, survey: 0, none: Math.max(0, n.firmsInNetwork - n.firmsWithResults) };
    const total = memberFirms(n);
    if (!n.findings)
        return [`${GLOSS.cdw} has reported on ${e.cdw} of the ${total} member firms.`];
    // From how many firms' lists a count comes (SFH-02).
    const lists = new Set(n.findings.map((f) => f.firmCode)).size;
    if (!n.findings.length)
        return [`${GLOSS.cdw} has reported on ${e.cdw} of the ${total} member firms; no findings list has been received yet.`];
    // From how many firms' lists the counts come (SFH-02), said when fewer lists than reports have arrived. DEC-53: one line.
    const from = lists < e.cdw ? ` (in the ${plural(lists, 'findings list')} received)` : '';
    return [`${GLOSS.cdw} has reported on ${e.cdw} of the ${total} member firms; ${findingsSince(n.findings, 'lists')}${from}.`];
}
/**
 * DEC-40: open findings grouped through the CDW family fallback are counted under a business risk like the others,
 * and said, with the label, on the page and in the PDF; null when there are none.
 */
export function familyNote(list) {
    const n = list ? findingCounts(list).byFamily : 0;
    // Plain words for the Board (NEXT PASS 3): what the label on the rows means.
    return n
        ? `${plural(n, 'open finding')} ${n === 1 ? 'is' : 'are'} matched to a business risk by the type of finding CDW reported, for National IT to check.`
        : null;
}
/** One name, on the page and in the PDF, for open findings not tied to the baseline controls (SFH-01). */
export const UNSORTED_LABEL = 'Not linked to a business risk yet';
/** A risk acceptance nothing links to a control yet (SFH-05): its impact is not known, not outside the four. */
export const ACCEPTANCE_UNLINKED_LABEL = 'Not linked to a control yet';
/**
 * Said after a view's main lines, never leading it (L1, SFH-01): open findings the CDW reports do not tie to the
 * baseline controls cannot be sorted by business risk yet.
 */
export function unsortedNote(list) {
    const n = list ? findingCounts(list).unlinked : 0;
    return n ? `${plural(n, 'open finding')} ${n === 1 ? 'is' : 'are'} not linked to a business risk yet.` : null;
}
/** The network tab's lines (L1): the range across firms, the goal and the gap to it, then the picked firm's place or what needs attention. */
/**
 * The typical firm's change since the stored quarter before this one (L1): like for like, so a quarter that counted
 * fully in place only is compared with today's fully figure.
 */
export function medianDirection(n, asOf) {
    const prev = earlierPoint(n.trend, asOf, (t) => t.btcMedian !== null && t.btcMedian !== undefined);
    if (!prev || n.btcMedian === null)
        return null;
    const moved = direction({ inPlace: n.btcMedian, fully: n.fullyMedians?.btc }, prev.btcMedian, prev.countsPartly);
    return moved ? `${moved} since ${quarterDate(prev.quarter)}` : null;
}
/**
 * The typical firm's change since the stored quarter before this one, said plainly in the network tab's opening (L1,
 * NEXT PASS 5): the figure it is about comes first, so no bracket is needed. A quarter that counted fully in place only
 * is set against today's fully figure, in the one wording (fullyChangePhrase, NEXT PASS 6): "; 38% fully, up 1 point
 * since June 30, 2026"; otherwise ", and up 5 points since June 30, 2026". Null when there is nothing to compare.
 */
function medianChangeClause(n, asOf) {
    const prev = earlierPoint(n.trend, asOf, (t) => t.btcMedian !== null && t.btcMedian !== undefined);
    if (!prev || n.btcMedian === null)
        return null;
    const c = likeForLike({ inPlace: n.btcMedian, fully: n.fullyMedians?.btc }, prev.btcMedian, prev.countsPartly);
    if (!c)
        return null;
    const moved = direction({ inPlace: n.btcMedian, fully: n.fullyMedians?.btc }, prev.btcMedian, prev.countsPartly);
    return `${c.fullyOnly ? '; ' : ', and '}${moved} since ${quarterDate(prev.quarter)}`;
}
/** The firms with CDW results below half in place (UX-07): their names when the reader may see them, else the count only. */
export function belowHalf(n) {
    const below = rankedDots(n.distribution).ranked.filter((x) => x.pct !== null && x.pct < 50);
    const names = below.map((x) => x.firmName).filter((x) => Boolean(x));
    return { count: below.length, names: below.length && names.length === below.length ? names : null };
}
export function networkLines(n, selected, asOf) {
    const isMe = (x) => Boolean(x.isYou || (selected !== null && x.firmCode === selected));
    const vals = rankedDots(n.distribution).ranked.map((x) => x.pct).filter((v) => v !== null);
    if (vals.length < 2)
        return null;
    const sorted = [...vals].sort((a, b) => b - a);
    // CDW said in plain words where the tab first names it, as the other tabs do (L1).
    // The gloss is bracketed and the subject named ("the share of"), so the gloss cannot run into it (NEXT PASS 6).
    const lines = [`Across ${vals.length} firms with results from ${GLOSS.cdw}, the share of ${GLOSS.btc} in place runs from ${pct(sorted.at(-1))} to ${pct(sorted[0])}.`];
    const moved = asOf ? medianChangeClause(n, asOf) : null;
    if (n.btcMedian !== null)
        lines.push(`Goal: ${GOALS.btc}; the typical firm, at ${pct(n.btcMedian)}, is ${Math.max(0, Math.round(100 - n.btcMedian))} points short${moved ?? ''}.`);
    const me = n.distribution.find(isMe);
    if (me && me.pct !== null) {
        const name = me.firmName ?? 'Your firm';
        lines.push(isApartDot(me) ? `${name}, at ${pct(me.pct)}, is ${me.source === 'self' ? 'self-assessed' : 'not from CDW'} and not ranked.` : `${name} is ${ordinalOf(sorted.indexOf(me.pct) + 1)} of ${vals.length}, at ${pct(me.pct)}.`);
    }
    else {
        // UX-07: on the National and Board views (names visible) the firms are named; a firm view keeps the count.
        // PL-08: no bare zero; with no firm below half, the line says so.
        const below = belowHalf(n);
        if (below.count)
            lines.push(`Needs attention: ${below.names ? joinNames(below.names) : plural(below.count, 'firm')} ${below.count === 1 ? 'has' : 'have'} less than half in place.`);
        else
            lines.push('Every firm with CDW results has at least half in place.');
    }
    return lines;
}
/** "Wave 2 is due December 3, 2026, in 59 days" (or "was due" once it has passed): Wave 2's goal date. */
function wave2Due(asOf) {
    const days = daysBetween(asOf, BTI_WAVE2_DUE);
    return `Wave 2 ${days >= 0 ? 'is' : 'was'} due ${longDate(BTI_WAVE2_DUE)}${days > 0 ? `, in ${plural(days, 'day')}` : ''}`;
}
/**
 * The BTI tab's lines for one firm (L1, NEXT PASS 6), as the other tabs do for a firm: line 1, the firm's own Wave 2
 * position with its change since the survey (both figures, DEC-45); line 2, the goal: Wave 2 by its due date, and
 * Wave 1 with the firm's own figure when the data has it; line 3, Wave 2's date and how many of its requirements the
 * firm does not have in place yet. Only the firm's own figures, which its members already receive: never National
 * IT's positions or BTI's evaluator comments (National IT and Board only).
 */
function firmBtiLines(f, asOf) {
    const w1 = f.headline.bti.wave1;
    const w2 = f.headline.bti.wave2;
    const s = f.source === null ? f.march2025Baselines : null;
    const size = BTI_WAVE2_ITEMS.length;
    // "BTI Wave 2" as its tile names it, with BTI said in plain words (L1); short, so the line fits the PDF's BTI row.
    const of = (total) => (total && total !== size ? `the ${total} rated requirements of ${GLOSS.bti} Wave 2` : `${GLOSS.bti} Wave 2`);
    const fullyOf = (m) => (m.total ? ((m.inPlace - m.partly) / m.total) * 100 : null);
    const change = !s && w2.value !== null ? changeBeside({ inPlace: w2.value, fully: fullyOf(w2) }, w2.march2025) : NO_CHANGE;
    const where = s
        ? s.btiWave2.pct === null
            ? `${f.firmName} has no 2026 results yet, and its March 2025 survey has no answers for ${of(0)}.`
            : `${f.firmName} has no 2026 results yet; its March 2025 survey answers put ${pct(s.btiWave2.pct)} of ${of(0)} in place.`
        : w2.value === null
            ? `${f.firmName} has no results yet for ${of(0)}.`
            : `${f.firmName} has ${pct(w2.value)} of ${of(w2.total)} in place${f.source === 'self' ? ' by its own rating' : ''}${change.text}.`;
    // Wave 1 against its goal, with the firm's own figure when the data has it.
    const w1Now = s ? (s.btiWave1.pct === null ? null : `${pct(s.btiWave1.pct)} in its March 2025 survey answers`) : w1.value === null ? null : `${pct(w1.value)} now`;
    const goals = tileGoals();
    const goal = `Goal: ${goals.btiWave2[0].toLowerCase()}${goals.btiWave2.slice(1)}; Wave 1, the first ${BTI_WAVE1_EVALUATION.items.length}: ${goals.btiWave1.toLowerCase()}${w1Now ? ` (${w1Now})` : ''}.`;
    // What needs attention: Wave 2's date, and what the firm does not have in place yet, from its own figures.
    const notIn = w2.total - w2.inPlace;
    // Every requirement fully in place only when every one is rated (SFH-01); otherwise say how many are not.
    const unrated = Math.max(0, size - w2.total);
    const notRated = unrated ? `${notIn || w2.partly ? ', and' : ';'} ${unrated} of ${size} ${unrated === 1 ? 'is' : 'are'} not assessed yet` : '';
    const left = s || w2.value === null
        ? ''
        : notIn
            ? `; ${notIn} of its ${w2.total}${unrated ? ' rated' : ''} requirements ${notIn === 1 ? 'is' : 'are'} not in place yet${notRated}`
            : w2.partly
                ? `; ${w2.partly} of its ${w2.total}${unrated ? ' rated' : ''} requirements ${w2.partly === 1 ? 'is' : 'are'} only partly in place${notRated}`
                : unrated
                    ? notRated
                    : '; every requirement is fully in place';
    return [where, goal, `Needs attention: ${wave2Due(asOf)}${left}.`];
}
/**
 * The BTI tab's lines (L1), for the picked firm (firmBtiLines) or the network: Wave 1 as BTI rated it, Wave 2 at the
 * typical firm with its change, and what needs attention, Wave 2's date first.
 */
export function btiLines(d) {
    if (d.firm)
        return firmBtiLines(d.firm, d.asOf);
    const n = d.network;
    const asOf = d.asOf;
    const c = btiVerdictCounts();
    // "Satisfied" is BTI's word in its own review, never ours (L1).
    // Wave 1 with its goal (accepted: every item in place) and its direction: BTI has reviewed it once, so there is no
    // earlier review to compare with, and the line says so (L1, NEXT PASS 5).
    const lines = [`${GLOSS.bti} marked ${c.satisfied} of its first ${BTI_WAVE1_EVALUATION.items.length} policy requirements, Wave 1, as satisfied in its one review so far; goal: ${tileGoals().btiWave1.toLowerCase()}.`];
    // Wave 2 at the typical firm, with both changes since the survey in the Summary's words (DEC-45, surveyChangePhrase).
    const wave2 = `Wave 2, the next ${BTI_WAVE2_ITEMS.length} requirements`;
    const change = n.btiWave2Median === null ? NO_CHANGE : changeBeside({ inPlace: n.btiWave2Median, fully: n.fullyMedians?.btiWave2 }, n.march2025BtiWave2Median);
    lines.push(n.btiWave2Median === null ? `${wave2}, has no figure at the typical firm yet.` : `${wave2}: ${pct(n.btiWave2Median)} in place at the typical firm${change.text}.`);
    // What needs attention: Wave 2's date (its goal), and National IT's positions on its requirements when known.
    const due = wave2Due(asOf);
    const w2 = n.btiPositions?.filter((p) => p.wave === 2);
    if (n.btiWave2 && w2) {
        const w = btiPositionCounts(w2, 2);
        const recorded = BTI_WAVE2_ITEMS.length - w.not_set;
        // Nothing is said as a bare zero (N4).
        lines.push(recorded
            ? `Needs attention: ${due}; National IT has assessed ${recorded} of the ${BTI_WAVE2_ITEMS.length} Wave 2 requirements so far; ${w.met ? `${w.met} ${w.met === 1 ? 'is' : 'are'} met` : 'none is met yet'}.`
            : `Needs attention: ${due}; National IT has not assessed any of the ${BTI_WAVE2_ITEMS.length} Wave 2 requirements yet.`);
    }
    else
        lines.push(`Needs attention: ${due}.`);
    return lines;
}
/** The network's decisions line, from the data (DEC-38): each firm decides its own, and how many wait with them. */
export function networkDecisionsLine(n) {
    const base = "Each firm's leaders decide on their own firm's risks and policies";
    const risks = n.riskAcceptances?.filter((a) => a.state === 'waiting').length;
    if (risks === undefined)
        return `${base}.`;
    // Every decision waiting at a firm (DEC-38 item 2): risk acceptances and policies to adopt, each from the data.
    const parts = [risks ? plural(risks, 'risk acceptance') : '', n.policyAdoptionsWaiting ? plural(n.policyAdoptionsWaiting, 'policy', 'policies') + ' to adopt' : ''].filter(Boolean);
    if (!parts.length)
        return `${base}; no decision waits with a firm's leaders.`;
    const total = risks + (n.policyAdoptionsWaiting ?? 0);
    return `${base}; ${joinNames(parts)} ${total === 1 ? 'is' : 'are'} waiting with firms' leaders.`;
}
/**
 * A tile's earlier figure from the March 2025 survey, with both changes (DEC-29, DEC-45), the same for a firm and the
 * network: "27% in the March 2025 survey; up 34 points since then (up 9 counting fully in place only)".
 */
function sinceSurvey(now, fullyNow, then, label) {
    if (then === null || then === undefined)
        return null;
    const c = surveyChange({ inPlace: now, fully: fullyNow }, then);
    return `${pct(then)} in the ${label}${c ? `; ${surveyChangePhrase(c, 'then')}` : ''}`;
}
/** The calendar quarter of an ISO date, as the snapshots name it ("2026-Q3"). */
export const quarterOfDate = (iso) => `${iso.slice(0, 4)}-Q${Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1}`;
/** The latest stored quarter before the quarter of `asOf` that has the value asked for: the earlier figure's quarter (L2). */
function earlierPoint(points, asOf, has) {
    const now = quarterOfDate(asOf);
    return [...points].filter((p) => p.quarter < now && has(p)).sort((a, b) => a.quarter.localeCompare(b.quarter)).at(-1) ?? null;
}
/**
 * The network quarter before this one, for a median: a quarter that counted fully in place only is set beside
 * today's fully figure, so the change of counting is not read as progress.
 */
function priorQuarter(trend, asOf, key, now) {
    const prev = earlierPoint(trend, asOf, (t) => t[key] !== null && t[key] !== undefined);
    if (!prev)
        return null;
    return quarterFigure(prev.quarter, prev[key], prev.countsPartly, now);
}
/** A stored quarter as an earlier figure, like for like (likeForLike): "June 30, 2026: 53% fully in place; up 2 points since then". */
function quarterFigure(quarter, then, countsPartly, now) {
    const c = likeForLike(now, then, countsPartly);
    return `${quarterDate(quarter)}: ${pct(then)} ${countsPartly ? 'in place' : 'fully in place'}${c ? `; ${movePhrase(c.points)} since then` : ''}`;
}
/** Said where a measure has no goal (L2: every tile says its goal, or that there is none). */
export const NO_GOAL = 'None set';
/**
 * Each headline measure's goal, one wording for the page and the PDF (L2, DEC-36). A function, not a constant: GOALS
 * comes from a module that is still loading when this one first runs (the vocabulary re-exports both).
 */
export const tileGoals = () => ({
    btc: GOALS.btc,
    btiWave1: 'Every requirement in place',
    btiWave2: `All ${BTI_WAVE2_ITEMS.length} requirements by ${longDate(BTI_WAVE2_DUE)}`,
    critical: GOALS.critical,
    plans: GOALS.plans,
    higherRisks: NO_GOAL,
    firms: NO_GOAL,
});
/**
 * Every headline tile's goal and earlier figure (L2), the one source for every tile group on the page (the Summary's
 * tiles and its detail, the network tab) and for the PDF's tiles: the same words per measure.
 */
export function headlineMeasures(d) {
    const e = earlierFigures(d);
    // A firm's BTC Baseline goal is the network's, and says so.
    const goals = tileGoals();
    const goal = (k) => (k === 'btc' && d.firm ? `${goals.btc} (network goal)` : goals[k]);
    return Object.fromEntries(Object.keys(goals).map((k) => [k, { goal: goal(k), earlier: e[k] }]));
}
/** Said where a measure has no earlier figure. */
export const NO_EARLIER = 'No earlier figure yet';
/**
 * The earlier figure under each headline measure (L2, DEC-29, DEC-37), the one source for the page's tiles and the
 * PDF's: the March 2025 survey (the firm's own answers; for the network the median of the CDW firms' answers,
 * labelled as the firms' own answers), else the stored quarter before this one (firm.quarterly_snapshot,
 * core.network_snapshot), else that there is none.
 */
export function earlierFigures(d) {
    if (d.firm) {
        const f = d.firm;
        const h = f.headline;
        const trend = f.trend ?? [];
        const plansThen = earlierPoint(trend, d.asOf, (t) => t.plansCommitted !== null && t.plansCommitted !== undefined);
        const plans = plansThen
            ? plansThen.plansCommitted
                ? `${quarterDate(plansThen.quarter)}: ${plansThen.plansOnTrack ?? 0} of ${plansThen.plansCommitted} on track or complete`
                : `${quarterDate(plansThen.quarter)}: no committed plans`
            : NO_EARLIER;
        const risksThen = earlierPoint(trend, d.asOf, (t) => t.higherRisksOpen !== null && t.higherRisksOpen !== undefined);
        const higherRisks = risksThen ? `${quarterDate(risksThen.quarter)}: ${risksThen.higherRisksOpen} open` : NO_EARLIER;
        const critThen = earlierPoint(trend, d.asOf, (t) => Boolean(t.criticalTotal));
        const critQuarter = critThen
            ? quarterFigure(critThen.quarter, (critThen.criticalInPlace / critThen.criticalTotal) * 100, critThen.countsPartly, {
                inPlace: h.critical.total ? (h.critical.inPlace / h.critical.total) * 100 : null,
                fully: h.critical.total ? ((h.critical.inPlace - h.critical.partly) / h.critical.total) * 100 : null,
            })
            : null;
        const base = { plans, higherRisks, firms: NO_EARLIER };
        if (f.source === null && f.march2025Baselines) {
            const s = 'This is the March 2025 figure; no 2026 rating yet';
            return { ...base, btc: s, btiWave1: f.march2025Baselines.btiWave1.surveyed ? s : 'Not in the March 2025 survey', btiWave2: f.march2025Baselines.btiWave2.surveyed ? s : 'Not in the March 2025 survey', critical: critQuarter ?? NO_EARLIER };
        }
        const fullyOf = (inPlace, partly, total) => (total ? ((inPlace - partly) / total) * 100 : null);
        const w2 = h.bti.wave2;
        const crit = h.critical;
        const critPct = crit.total ? (crit.inPlace / crit.total) * 100 : null;
        return {
            ...base,
            btc: sinceSurvey(h.btc.value, fullyOf(h.btc.inPlace, h.btc.partly, h.btc.total), h.btc.march2025, 'March 2025 survey') ?? 'No March 2025 survey figure',
            btiWave1: sinceSurvey(h.bti.wave1.value, fullyOf(h.bti.wave1.inPlace, h.bti.wave1.partly, h.bti.wave1.total), h.bti.wave1.march2025, 'March 2025 survey') ?? NO_EARLIER,
            btiWave2: sinceSurvey(w2.value, fullyOf(w2.inPlace, w2.partly, w2.total), w2.march2025, 'March 2025 survey') ?? NO_EARLIER,
            critical: sinceSurvey(critPct, fullyOf(crit.inPlace, crit.partly, crit.total), crit.march2025Pct, 'March 2025 survey') ?? critQuarter ?? NO_EARLIER,
        };
    }
    const n = d.network;
    // The firms' own March 2025 answers, with both changes since (DEC-45: the network too).
    const fm = n.fullyMedians;
    const survey = (now, fully, then) => sinceSurvey(now, fully, then, MARCH_2025_NETWORK_LABEL);
    const plansThen = earlierPoint(n.trend, d.asOf, (t) => t.plansOnTrackPct !== null && t.plansOnTrackPct !== undefined);
    const firmsThen = earlierPoint(n.trend, d.asOf, (t) => t.firmsWithResults !== null && t.firmsWithResults !== undefined);
    return {
        btc: survey(n.btcMedian, fm?.btc, n.march2025BtcMedian) ?? NO_EARLIER,
        btiWave1: survey(n.btiWave1Median, fm?.btiWave1, n.march2025BtiWave1Median) ?? NO_EARLIER,
        btiWave2: survey(n.btiWave2Median, fm?.btiWave2, n.march2025BtiWave2Median) ?? NO_EARLIER,
        critical: survey(n.criticalMedianPct, fm?.critical, n.march2025CriticalMedian) ?? priorQuarter(n.trend, d.asOf, 'criticalMedianPct', { inPlace: n.criticalMedianPct, fully: n.fullyMedians?.critical }) ?? NO_EARLIER,
        plans: plansThen ? `${quarterDate(plansThen.quarter)}: ${pct(plansThen.plansOnTrackPct)} at the typical firm` : NO_EARLIER,
        // The network has no higher-risks tile; the field is kept so the page and the PDF read one shape.
        higherRisks: NO_EARLIER,
        firms: firmsThen ? `${quarterDate(firmsThen.quarter)}: ${firmsThen.firmsWithResults} firms` : NO_EARLIER,
    };
}
/**
 * The meaning line of a findings list, on the page and the PDF: what the list is, with no counts (DEC-55 item 6). It
 * takes the counts only to tell an empty list from one with open findings.
 */
/**
 * A finding's or an accepted risk's impact, with CDW's rating folded in (NEXT PASS 5, F4): each row is only the risk,
 * the firm and this line. "Impact: Client data exposure; rated high or very high by CDW".
 */
export const impactText = (label, higher) => `Impact: ${label}${higher ? '; rated high or very high by CDW' : ''}`;
export function findingsLine(c) {
    // DEC-49/53 (PL-04): no owner or due-date wording. DEC-55 item 6 (NEXT PASS 3, F4): one plain meaning line about the
    // open findings and what they could lead to, with no status counts (repeats, closed, confirmed by National IT review);
    // firms see those on their own pages. The findings not linked or matched by type are said once, in their own notes.
    return c.open ? 'Each open finding is listed with the business risk it could lead to.' : 'No finding is open.';
}
//# sourceMappingURL=leadership-lines.js.map